import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useParams } from 'react-router-dom';
import FinancialService from '../../../../../AppSettings/services/FinancialService';
import MatterService from '../../../../../AppSettings/services/MatterService';
import VenExpService from '../../../../../AppSettings/services/VenExpService';
import OppExpService from '../../../../../AppSettings/services/OppExpService';
import GenExpensesService from '../../../../../AppSettings/services/GenExpensesService';
import FixBillService from '../../../../../AppSettings/services/FixBillService';
import SectionCard from '../../../../../Components/SectionCard/SectionCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/free-solid-svg-icons';
import './BillingExpensesPie.css';
import { normalizeToArray } from '../../../../../helpers/responseHelpers';
import { computeBilledTimeAmount } from '../../../../../helpers/billingHelpers';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

// The matter budget is stored as a number of hours (see "Project Budget (Hrs)" on the
// matter form), so render it as hours + minutes to match the timesheet, not as currency.
function formatHoursToHoursMinutes(hours: number): string {
  if (hours <= 0) return '0h 00m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

const BillingExpensesPie: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const [billableTimeTotal, setBillableTimeTotal] = useState<number>(0);
  const [fixedFeesTotal, setFixedFeesTotal] = useState<number>(0);
  const [otherTotal, setOtherTotal] = useState<number>(0);
  const [vendorTotal, setVendorTotal] = useState<number>(0);
  const [operatingTotal, setOperatingTotal] = useState<number>(0);
  const [generalTotal, setGeneralTotal] = useState<number>(0);
  const [fixedBillsTotal, setFixedBillsTotal] = useState<number>(0);
  const [budget, setBudget] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllData = async (): Promise<void> => {
      if (!matterId) return;
      
      try {
        setIsLoading(true);
        setError(null);

        // Fetch billing data using matter-specific endpoints and normalize
        const billedTimeData = await FinancialService.getBilledTime(matterId);
        const billedEntries = normalizeToArray(billedTimeData);
        const billableTimeSum = billedEntries.reduce((sum: number, item: any) =>
          sum + computeBilledTimeAmount(item), 0
        );

        const fixedFeesData = await FinancialService.getAssignedFixedFees(matterId);
        const fixedFeesList = normalizeToArray(fixedFeesData);
        const fixedFeesSum = fixedFeesList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.fees))) ? parseFloat(String(item.fees)) : 0), 0
        );

        const otherBillingData = await FinancialService.getOtherBillingByMatter(matterId);
        const otherBillingList = normalizeToArray(otherBillingData);
        const otherSum = otherBillingList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        // Fetch expenses data using matter-specific endpoints
        const vendorData = await VenExpService.getVenExpensesByMatter(matterId);
        const vendorList = normalizeToArray(vendorData);
        const vendorSum = vendorList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        const operatingData = await OppExpService.getOppExpensesByMatter(matterId);
        const operatingList = normalizeToArray(operatingData);
        const operatingSum = operatingList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        const generalData = await GenExpensesService.getGenExpensesByMatter(matterId);
        const generalList = normalizeToArray(generalData);
        const generalSum = generalList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        const fixedBillsData = await FixBillService.getFixBillExpensesByMatter(matterId);
        const fixedBillsList = normalizeToArray(fixedBillsData);
        const fixedBillsSum = fixedBillsList.reduce((sum: number, item: any) => 
          sum + (Number.isFinite(parseFloat(String(item.amount))) ? parseFloat(String(item.amount)) : 0), 0
        );

        // Matter budget (stored on the matter record). Tolerate a failed/empty fetch so a
        // missing budget never breaks the billing/expenses breakdown.
        const matterDetails = await MatterService.getMatterDetails(matterId).catch(() => null);
        const rawBudget = matterDetails?.budget;
        const parsedBudget =
          rawBudget != null && String(rawBudget).trim() !== ''
            ? parseFloat(String(rawBudget).trim())
            : NaN;

        // Set all the totals
        setBillableTimeTotal(billableTimeSum);
        setFixedFeesTotal(fixedFeesSum);
        setOtherTotal(otherSum);
        setVendorTotal(vendorSum);
        setOperatingTotal(operatingSum);
        setGeneralTotal(generalSum);
        setFixedBillsTotal(fixedBillsSum);
        setBudget(Number.isFinite(parsedBudget) ? parsedBudget : null);

      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to fetch data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    if (matterId) {
      fetchAllData();
    }
  }, [matterId]);

  const totalBilling = billableTimeTotal + fixedFeesTotal + otherTotal;
  const totalExpenses = vendorTotal + operatingTotal + generalTotal + fixedBillsTotal;
  const netIncome = totalBilling - totalExpenses;
  const grandTotal = totalBilling + totalExpenses;

  const billingPercentage = grandTotal > 0 ? (totalBilling / grandTotal) * 100 : 0;
  const expensesPercentage = grandTotal > 0 ? (totalExpenses / grandTotal) * 100 : 0;

  const data = {
    labels: [
      'Billable Time',
      'Fixed Fees',
      'Other',
      'Vendor Services',
      'Operating',
      'General',
      'Fixed Bills',
    ],
    datasets: [
      {
        label: 'Amount',
        data: [
          billableTimeTotal,
          fixedFeesTotal,
          otherTotal,
          vendorTotal,
          operatingTotal,
          generalTotal,
          fixedBillsTotal,
        ],
        backgroundColor: [
          'rgba(30, 79, 251, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(96, 165, 250, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(248, 113, 113, 0.8)',
          'rgba(252, 165, 165, 0.8)',
          'rgba(254, 202, 202, 0.8)',
        ],
        borderColor: [
          'rgba(30, 79, 251, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(96, 165, 250, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(248, 113, 113, 1)',
          'rgba(252, 165, 165, 1)',
          'rgba(254, 202, 202, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right' as const,
        labels: {
          padding: 8,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 10
          },
          boxWidth: 8,
          boxHeight: 8
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: $${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Icon component for top right
  const iconElement = (
    <div className="billingExpensesPie-icon-container">
      <FontAwesomeIcon icon={faCoins} className="billingExpensesPie-icon" />
    </div>
  );

  if (isLoading) {
    return (
      <SectionCard title="Billing vs. Expenses" right={iconElement}>
        <div>Loading...</div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Billing vs. Expenses" right={iconElement}>
        <div>Error: {error}</div>
      </SectionCard>
    );
  }

  return (
    <SectionCard 
      title="Billing vs. Expenses" 
      right={iconElement}
      className="billingExpensesPie-container"
    >
      <div className="billingExpensesPie-content">
        <div className="billingExpensesPie-chart-wrapper">
          <Pie data={data} options={options} />
        </div>
        <div className="billingExpensesPie-summary">
          <div className="billingExpensesPie-summary-item">
            <div className="billingExpensesPie-summary-label">
              <span className="billingExpensesPie-dot billingExpensesPie-dot--billing"></span>
              <span>Total Billing</span>
            </div>
            <span className="billingExpensesPie-summary-value">{billingPercentage.toFixed(1)}%</span>
          </div>
          <div className="billingExpensesPie-summary-item">
            <div className="billingExpensesPie-summary-label">
              <span className="billingExpensesPie-dot billingExpensesPie-dot--expenses"></span>
              <span>Total Expenses</span>
            </div>
            <span className="billingExpensesPie-summary-value">{expensesPercentage.toFixed(1)}%</span>
          </div>
          <div className="billingExpensesPie-summary-item">
            <div className="billingExpensesPie-summary-label">
              <span className="billingExpensesPie-dot billingExpensesPie-dot--income"></span>
              <span>Net Income</span>
            </div>
            <span className="billingExpensesPie-summary-value">${netIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="billingExpensesPie-summary-item">
            <div className="billingExpensesPie-summary-label">
              <span className="billingExpensesPie-dot billingExpensesPie-dot--budget"></span>
              <span>Budget</span>
            </div>
            <span className="billingExpensesPie-summary-value">{formatHoursToHoursMinutes(budget ?? 0)}</span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default BillingExpensesPie;
